// Transcript renderer shared by any surface that shows an agent conversation.
// Emits the SAME markup + classes as the Sessions viewer (.turn/.role/.text/.tools/
// .tool-block/…) so it renders identically, and owns the collapse-toggle wiring so
// callers only render + wire. Input is the normalized message shape the stores keep:
// { role, text, tool_calls:[{name,input}], results:[…] }.
import { esc, renderMd } from './core'

const PREVIEW = 1200 // chars shown before a "Show more" fold
const FULL_INLINE = 2000 // texts at/under this render whole, no fold
const CMD_LINES = 2 // command inputs longer than this collapse
const OUT_CAP = 4000 // tool output hard cap (a transcript viewer, not a log)

/** The most useful single line from a tool call's input object. Agents name the
 *  "primary" argument differently (file_path vs filePath, pattern vs filePattern),
 *  so probe the common keys across those conventions before falling back to raw JSON. */
const CMD_KEYS = [
  'command', 'file_path', 'filePath', 'pattern', 'filePattern', 'glob',
  'path', 'filepath', 'url', 'query', 'description', 'prompt',
]
function cmdOf(input: any): string {
  if (input == null) return ''
  if (typeof input === 'string') return input
  for (const k of CMD_KEYS) { if (input[k]) return String(input[k]) }
  try { return JSON.stringify(input) } catch { return '' }
}

/** Message text: whole when short, else preview + hidden full behind a toggle.
 *  `md` renders assistant prose as Markdown; user prompts stay plain-escaped. */
function textBlock(text: any, md: boolean): string {
  const t = String(text || '')
  if (!t) return ''
  const render = md ? renderMd : esc
  const cls = 'text' + (md ? ' md' : '')
  if (t.length <= FULL_INLINE) return '<div class="' + cls + '">' + render(t) + '</div>'
  return '<div class="' + cls + ' msg"><span class="msg-prev">' + render(t.slice(0, PREVIEW)) + ' …</span>' +
    '<span class="msg-full">' + render(t) + '</span></div>' +
    '<button class="msg-more" type="button">Show more ↓</button>'
}

function cmdBlock(cmd: string): string {
  if (!cmd) return ''
  const lines = cmd.split('\n')
  if (lines.length > CMD_LINES) {
    return '<div class="tool-block-cmd collapsible">' +
      '<pre class="tool-cmd-pre">' + esc(lines.slice(0, CMD_LINES).join('\n')) + '</pre>' +
      '<pre class="tool-cmd-full">' + esc(cmd) + '</pre>' +
      '<button class="tool-cmd-toggle" type="button"><span class="tool-cmd-arrow">&#9654;</span> expand</button></div>'
  }
  return '<div class="tool-block-cmd"><pre class="tool-cmd-pre">' + esc(cmd) + '</pre></div>'
}

/** One tool call: name · command · (collapsed output); `note`, a caller's mark beside the command. */
function toolBlock(name: string, cmd: string, output: unknown, note = ''): string {
  const has = output != null && String(output).length > 0
  const toggle = has ? '<button class="tool-out-toggle" type="button">output</button>' : ''
  const body = has ? '<div class="tool-block-body"><pre class="tool-output-pre">' + esc(String(output).slice(0, OUT_CAP)) + '</pre></div>' : ''
  return '<div class="tool-block"><div class="tool-block-row">' +
    '<span class="tool-block-name">' + esc(name || 'tool') + '</span>' + cmdBlock(cmd) + note + toggle + '</div>' + body + '</div>'
}

/** A standalone tool result (a `tool_result` message with no matching call). */
function outputBlock(output: unknown): string {
  return '<div class="tool-block"><div class="tool-block-row">' +
    '<span class="tool-block-name">result</span><button class="tool-out-toggle" type="button">output</button></div>' +
    '<div class="tool-block-body"><pre class="tool-output-pre">' + esc(String(output).slice(0, OUT_CAP)) + '</pre></div></div>'
}

/** `role` is markup this file writes ("You #2"), never message text. */
function turn(cls: string, role: string, body: string): string {
  return '<div class="turn ' + cls + '"><div class="role">' + role + '</div>' + body + '</div>'
}

/** `note(name, input)`: HTML to mark a tool call with (Replay marks the calls the environment answered). */
export function renderTranscript(messages: any[], note?: (name: string, input: unknown) => string): string {
  if (!messages || !messages.length) return '<div class="empty">No transcript captured.</div>'
  let userN = 0
  return messages.map((m) => {
    const role = m.role || 'assistant'
    const tools = (m.tool_calls || []) as any[]
    const results = (m.results || []) as unknown[]
    if (role === 'user') { userN++; return turn('user', 'You <span class="tnum">#' + userN + '</span>', textBlock(m.text, false)) }
    if (role === 'tool_result' || (!m.text && !tools.length && results.length)) {
      return results.length ? turn('asst toolrun', 'Tool output', results.map(outputBlock).join('')) : ''
    }
    // assistant: text + its tool calls (pair a result to each call by position; any
    // extra results render as standalone output blocks).
    const toolsHtml = tools.length || results.length
      ? '<div class="tools">' + tools.map((t, i) => toolBlock(t.name, cmdOf(t.input), results[i], note ? note(t.name, t.input) : '')).join('') +
        results.slice(tools.length).map(outputBlock).join('') + '</div>'
      : ''
    const body = (m.text ? textBlock(m.text, true) : '') + toolsHtml
    return body ? turn('asst', 'Assistant', body) : ''
  }).join('')
}

/** Wire the collapse toggles (idempotent per element via a flag). One delegated
 *  listener on the container: tool output, long command, and long-message folds. */
export function wireTranscript(root: HTMLElement): void {
  if ((root as any)._txWired) return
  ;(root as any)._txWired = true
  root.addEventListener('click', (e) => {
    const el = e.target as HTMLElement
    const out = el.closest('.tool-out-toggle') as HTMLElement | null
    if (out) {
      out.classList.toggle('on')
      const body = out.closest('.tool-block')?.querySelector('.tool-block-body')
      if (body) body.classList.toggle('on')
      return
    }
    const cmd = el.closest('.tool-cmd-toggle') as HTMLElement | null
    if (cmd) {
      const wrap = cmd.closest('.tool-block-cmd')
      if (wrap) {
        wrap.classList.toggle('on')
        const arrow = wrap.querySelector('.tool-cmd-arrow') as HTMLElement | null
        if (arrow) arrow.style.transform = wrap.classList.contains('on') ? 'rotate(90deg)' : ''
        const btn = wrap.querySelector('.tool-cmd-toggle')
        if (btn) btn.lastChild!.textContent = wrap.classList.contains('on') ? ' collapse' : ' expand'
      }
      return
    }
    const more = el.closest('.msg-more') as HTMLElement | null
    if (more) {
      const msg = more.previousElementSibling as HTMLElement | null
      if (msg && msg.classList.contains('msg')) {
        msg.classList.toggle('on')
        more.textContent = msg.classList.contains('on') ? 'Show less ↑' : 'Show more ↓'
      }
    }
  })
}
